import { useEffect, useRef, useState } from 'react';
import { Environment } from '../environment.ts';
import axios from "axios";
import { Environment as environment } from "../environment.ts";
import { toast } from 'react-toastify';
import { useFlowStore } from "../store/FlowStateStore.ts";

const ACCEPT_MAP: Record<string, string> = {
    IMAGE: "image/*",
    VIDEO: "video/*",
    AUDIO: "audio/*",
    DOCUMENT: ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt",
};

// WhatsApp Cloud API per-file size limits.
const MAX_SIZE_MB: Record<string, number> = {
    IMAGE: 5,
    VIDEO: 16,
    AUDIO: 16,
    DOCUMENT: 100,
};

const TAB_TO_TYPE: Record<string, string> = {
    Image: "IMAGE",
    Video: "VIDEO",
    Audio: "AUDIO",
    File: "DOCUMENT",
};

const TYPE_TO_TAB: Record<string, string> = {
    IMAGE: "Image",
    VIDEO: "Video",
    AUDIO: "Audio",
    DOCUMENT: "File",
};

const AddMedia = ({setVisible, setSelectedMedia, mediaType = "IMAGE"}) => {

    const { sasToken, accountName, containerName } = Environment;
    const [selectedTag, setSelectedTab] = useState<string>(TYPE_TO_TAB[mediaType] || "Image");
    const [uploadedMedia, setUploadedMedia] = useState<any[]>([]);
    const [selectedMediaFile, setSelectedMediaFile] = useState<any>();
    const [isUploading, setIsUploading] = useState(false);
    // Audio isn't offered here: this dialog is only ever opened from Media Button
    // nodes, and WhatsApp's interactive button messages don't support an audio header.
    const TABS = [/* "Product", */ "Image", /* "Audio", */ "Video", "File"];
    const { businessId } = useFlowStore();

    const fileInputRef = useRef(null);

    const isProductTab = selectedTag === "Product";
    const activeMediaType = TAB_TO_TYPE[selectedTag];

    useEffect(()=> {
        getMedia();
    }, []);

    const getMedia = () => {
        axios.get(`${environment.baseUrl}/get/media?businessId=${businessId}`).then((response: any) => {
            setUploadedMedia(!!response.data.data ? response.data.data.mediaArrayList : []);
        }).catch((error) => {
            toast.error(error.response?.data?.message || "Failed to load media.");
        })
    }

    const uploadMedia = (url: string, type: string) => {
        const payload = {
            url: url,
            mediaType: type
        }
        return axios.put(`${environment.baseUrl}/upload/media?businessId=${businessId}`, payload).then(() => {
            toast.success(`Media Uploaded.`);
            getMedia();
        }).catch((error) => {
            toast.error(error.response?.data?.message || "Failed to register media.");
        })
    }
    const deleteMedia = () => {
        if (!selectedMediaFile?.id) {
            toast.error("Select a media item to delete.");
            return;
        }
        axios.delete(`${environment.baseUrl}/delete/media?businessId=${businessId}&mediaId=${selectedMediaFile.id}`).then(() => {
            toast.success(`Media Deleted.`);
            setSelectedMediaFile(null);
            getMedia();
        }).catch((error) => {
            toast.error(error.response?.data?.message || "Failed to delete media.");
        })
    }
    const insertMediaFile = () => {
        setSelectedMedia(selectedMediaFile);
        setVisible(false);
    }

    const handleButtonClick = () => {
        if (isProductTab) return;
        (fileInputRef.current as any)?.click();
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const type = activeMediaType || mediaType;
        const maxSizeMb = MAX_SIZE_MB[type] ?? MAX_SIZE_MB.IMAGE;
        if (file.size > maxSizeMb * 1024 * 1024) {
            toast.error(`${type} files must be under ${maxSizeMb}MB.`);
            event.target.value = "";
            return;
        }
        setIsUploading(true);
        try {
            const blobName = `${Date.now()}-${encodeURIComponent(file.name)}`;

            const uploadUrl = `https://${accountName}.blob.core.windows.net/${containerName}/${blobName}${sasToken}`;

            const response = await fetch(uploadUrl, {
                method: "PUT",
                headers: {
                    "x-ms-blob-type": "BlockBlob",
                    "Content-Type": file.type,
                },
                body: file,
            });

            if (!response.ok) {
                throw new Error(`Upload failed: ${response.status} - ${response.statusText}`);
            }

            const blobUrl = `https://${accountName}.blob.core.windows.net/${containerName}/${blobName}`;
            await uploadMedia(blobUrl, type);
        } catch (err) {
            console.error("Upload error:", err);
            toast.error("Upload failed.");
        } finally {
            setIsUploading(false);
            event.target.value = "";
        }
    };
    const selectMedia = (image: any) => {
        if (image.url == selectedMediaFile?.url) {
            setSelectedMediaFile(null);
            return;
        }
        setSelectedMediaFile(image);
    }

    const filteredMedia = isProductTab
        ? []
        : uploadedMedia?.filter((image: any) => (image.mediaType || "IMAGE") === activeMediaType);

    const getDisplayName = (url: string): string => {
        if (!url) return "";
        try {
            const segment = decodeURIComponent(url.split("/").pop()?.split("?")[0] || "");
            return segment.replace(/^\d+-/, ""); // strip the "<timestamp>-" upload prefix
        } catch {
            return url.split("/").pop()?.split("?")[0] || "";
        }
    };

    const renderThumbnail = (image: any) => {
        const type = image.mediaType || "IMAGE";
        if (type === "VIDEO") {
            return <video src={image.url} muted />;
        }
        if (type === "AUDIO") {
            return (
                <div className="thumb-fallback flex flex-col items-center justify-center">
                    <span style={{ fontSize: 20 }}>🎵</span>
                    <span className="font-label" style={{ fontSize: 8, color: '#64748b', wordBreak: 'break-all', textAlign: 'center', lineHeight: 1.1 }}>{getDisplayName(image.url)}</span>
                </div>
            );
        }
        if (type === "DOCUMENT") {
            return (
                <div className="thumb-fallback flex flex-col items-center justify-center">
                    <span style={{ fontSize: 20 }}>📄</span>
                    <span className="font-label" style={{ fontSize: 8, color: '#64748b', wordBreak: 'break-all', textAlign: 'center', lineHeight: 1.1 }}>{getDisplayName(image.url)}</span>
                </div>
            );
        }
        return <img src={image.url} />;
    };

    return (
        <div className="media-container">
            <div className="flex flex-row justify-between">
                <div className="search-container">
                    <img src={require("../assets/common/search.png")} style={{ height: '15px' }} />
                    <input type="text" placeholder="Search media" />
                </div>
                {!isProductTab && (
                    <div className="flex flex-row">
                        <button className="flex flex-row action-btn" disabled={isUploading} onClick={handleButtonClick}>
                            {isUploading ? (
                                <i className="fa fa-circle-o-notch fa-spin" style={{ marginRight: '5px' }}></i>
                            ) : (
                                <img src={require("../assets/common/upload.png")} style={{ height: '20px', width: '20px', marginRight: '5px' }} />
                            )}
                            <span>{isUploading ? "Uploading..." : "Upload"}</span>
                        </button>
                        <button className="flex flex-row action-btn" disabled={!selectedMediaFile || isUploading} onClick={()=>deleteMedia()}>
                            <img src={require("../assets/common/delete.png")} style={{ height: '20px', width: '20px', marginRight: '5px' }} />
                            <span>Delete</span>
                        </button>
                    </div>
                )}
            </div>
            <div>
                <div className='media-tab-container'>
                    {
                        TABS.map((tab) => {
                            return (
                                <div
                                    key={tab}
                                    className='media-tab'
                                    style={{ fontWeight: (tab == selectedTag ? '800' : '400'), color: (tab == selectedTag ? 'black' : 'lightgray'), cursor: 'pointer' }}
                                    onClick={() => { setSelectedTab(tab); setSelectedMediaFile(null); }}
                                >{tab}</div>
                            )
                        })
                    }
                </div>
                {!isProductTab && (
                    <div className="font-label" style={{ margin: '4px 0', color: '#94a3b8', fontSize: 11 }}>
                        Max file size: {MAX_SIZE_MB[activeMediaType]}MB
                    </div>
                )}
                <div className='media-tab-content'>
                    <div className='media-tab-content-list'>
                        {isProductTab ? (
                            <div className='empty-screen-content'>
                                <span className="font-label" style={{ textAlign: 'center', padding: '0 16px' }}>
                                    Catalog products aren't managed here — use the "Single Product" or "Option List" node from the sidebar to attach items from your connected catalog.
                                </span>
                            </div>
                        ) : filteredMedia?.length > 0 ? (
                            <div className='media-tab-image-list'>
                                <div className='image-list'>
                                    {filteredMedia?.map((image: any, idx)=> {
                                        return (
                                            <div key={image.id || idx} className={selectedMediaFile?.url == image.url ? 'selected-image' : ''} onClick={()=>selectMedia(image)}>
                                                {renderThumbnail(image)}
                                            </div>
                                        )
                                    })}
                                </div>
                                <button disabled={!selectedMediaFile} onClick={()=>insertMediaFile()}>Use</button>
                            </div>
                        ) :
                        (<div className='empty-screen-content'>
                            <img src={require("../assets/common/empty.jpeg")} style={{ height: '200px', width: '300px', margin: "10px 0px" }} />
                            <button className="flex flex-row action-btn" disabled={isUploading} onClick={handleButtonClick}>
                                {isUploading && <i className="fa fa-circle-o-notch fa-spin" style={{ marginRight: '5px' }}></i>}
                                <span>{isUploading ? "Uploading..." : "Upload"}</span>
                            </button>
                        </div>)}
                    </div>
                </div>
            </div>
            <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept={ACCEPT_MAP[activeMediaType] ?? "*"}
                style={{ display: "none" }}
            />
        </div>
    )
}

export default AddMedia;
